/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { observer } from 'mobx-react';
import { Box, IconButton, MenuItem, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/MoreHoriz';
import React from 'react';
import ChatViewStyles from '../ChatViewStyles';
import { ChatMessageViewModel } from './ChatMessageViewModel';
import { useViewModel } from '../../../../../hooks/useViewModel';
import { Message } from '../../../../../types/Message';
import { FormattedMessage } from 'react-intl';
import Menu from '@mui/material/Menu';

interface Props {
	message: Message;
}

const ChatMessageView = observer((props: Props) => {
	const { classes } = ChatViewStyles();
	const viewModel: ChatMessageViewModel = useViewModel(ChatMessageViewModel);
	const { message } = props;
	const weekDayIndicator = viewModel.getWeekDayIndicator(message.identifier);
	const dateSeparator = viewModel.getDateSeparator(message.identifier);
	const profileImageAlt = viewModel.formatMessage('chat.profile_image_alt');
	const sharedEntryAlt = viewModel.formatMessage('chat.shared_entry_alt');

	return (
		<>
			<Box className={classes.message}>
				<img
					src={viewModel.getSenderImgUrl(message)}
					alt={message.username + profileImageAlt}
					className={classes.image}
				/>
				<Box className={classes.chatTextContainer}>
					<Typography className={classes.chatUsername}>{message.username}</Typography>
					{message.text ? (
						<Typography className={classes.chatText}>{message.text}</Typography>
					) : (
						<img
							className={classes.entryImage}
							src={message.sharedEntryThumbnail}
							alt={sharedEntryAlt}
							onClick={() => {
								viewModel.openEntryDetailView(message.sharedEntryId);
							}}
						/>
					)}
				</Box>
				<Box className={classes.timestampContainer}>
					<Typography className={classes.timestamp}>{viewModel.getTime(message.identifier)}</Typography>
					<IconButton
						onClick={(event) => {
							viewModel.menu.toggleMenu(event);
							viewModel.setMessageToDelete(message.identifier);
						}}
						className={classes.messageButton}>
						<MenuIcon />
					</IconButton>
				</Box>
				<Menu
					id='message-menu'
					anchorEl={viewModel.menu.anchor}
					keepMounted
					open={viewModel.menu.open}
					onClose={viewModel.menu.closeMenu}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
					transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
					<MenuItem
						onClick={() => {
							viewModel.deleteMessage();
							viewModel.menu.closeMenu();
						}}
						className={classes.deleteButton}>
						<FormattedMessage id={'chat.delete_message'} />
					</MenuItem>
				</Menu>
			</Box>
			{message.newDay && (
				<Typography className={classes.daySeparator}>
					<FormattedMessage id={'chat.weekDays.' + weekDayIndicator} />
					<span>{dateSeparator}</span>
				</Typography>
			)}
		</>
	);
});

export default ChatMessageView;
